import { useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, UserCheck, Mail, MessageCircle, ShieldCheck, MapPin, 
  Clock, CheckCircle2 
} from "lucide-react";
import { DPO_INFO } from "@/lib/security";

export default function DPOPage() {
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    if (hadDark) {
      root.classList.remove("dark");
      root.classList.add("light");
    }
    return () => {
      if (hadDark) {
        root.classList.remove("light");
        root.classList.add("dark");
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EAF4FF] via-[#F5FAFF] to-[#E6F0FF] text-slate-900 font-sans selection:bg-blue-200 relative overflow-hidden pb-20">
      
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-[10%] left-[-5%] size-[500px] rounded-full bg-[radial-gradient(circle_at_center,#33CCFF22,transparent_60%)] blur-3xl" />
        <div className="absolute bottom-[20%] right-[-5%] size-[400px] rounded-full bg-[radial-gradient(circle_at_center,#1A66CC11,transparent_60%)] blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-12 relative z-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700 mb-8 group transition-all"
        >
          <div className="size-8 rounded-lg bg-white shadow-sm border border-blue-100 flex items-center justify-center group-hover:-translate-x-1 transition-transform">
            <ArrowLeft className="size-4" />
          </div>
          Voltar para o Início
        </Link>

        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-br from-[#33CCFF]/10 to-[#1A66CC]/5 rounded-[2.5rem] blur-2xl opacity-50" />
          
          <div className="relative bg-white/60 backdrop-blur-3xl rounded-[2rem] border border-white/80 p-8 lg:p-12 shadow-[0_30px_80px_-20px_rgba(26,102,204,0.15),inset_0_1px_1px_rgba(255,255,255,1)]">
            
            <header className="flex flex-col sm:flex-row sm:items-center gap-5 mb-16 pb-8 border-b border-blue-100/50">
              <div className="size-14 rounded-2xl bg-gradient-to-br from-[#33CCFF] to-[#1A66CC] grid place-items-center text-white shadow-lg shadow-blue-500/30">
                <UserCheck className="size-7" />
              </div>
              <div>
                <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-1">DPO e Contato</h1>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  Encarregado de Proteção de Dados
                </p>
              </div>
            </header>

            <div className="grid lg:grid-cols-[1fr_0.8fr] gap-12">
              <div className="space-y-10">
                <section className="space-y-6">
                  <h2 className="text-xl font-black text-slate-900 m-0">O que é o DPO?</h2>
                  <p className="text-sm text-slate-600 leading-relaxed italic">
                    O Encarregado pelo Tratamento de Dados Pessoais (DPO) é o elo de comunicação entre a nossa plataforma, os usuários e a Autoridade Nacional de Proteção de Dados (ANPD).
                  </p>
                </section>

                <div className="space-y-6">
                  <h2 className="text-xl font-black text-slate-900 m-0">Como podemos ajudar?</h2>
                  <div className="grid gap-3">
                    {[
                      "Solicitar acesso aos seus dados pessoais",
                      "Pedir a correção de dados incompletos ou inexatos",
                      "Solicitar a exclusão definitiva da sua conta",
                      "Dúvidas sobre o processamento local de XMLs",
                      "Reportar uma possível vulnerabilidade"
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 text-xs font-bold text-slate-700 bg-white/40 p-4 rounded-xl border border-white/60 shadow-sm">
                        <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-slate-900 text-white p-8 rounded-3xl shadow-xl space-y-8 relative overflow-hidden">
                  <ShieldCheck className="absolute -right-6 -top-6 size-32 opacity-10" />
                  
                  <div className="space-y-1 relative z-10">
                    <p className="text-[10px] font-black uppercase tracking-widest text-blue-400">Encarregado Atual</p>
                    <p className="text-xl font-black">{DPO_INFO.nome}</p>
                  </div>

                  <div className="space-y-4 relative z-10">
                    <a href={`mailto:${DPO_INFO.email}`} className="flex items-center gap-4 group">
                      <div className="size-10 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-blue-500 transition-colors">
                        <Mail className="size-5" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">E-mail Direto</p>
                        <p className="text-sm font-bold group-hover:text-blue-400 transition-colors">{DPO_INFO.email}</p>
                      </div>
                    </a>

                    <div className="flex items-center gap-4">
                      <div className="size-10 rounded-xl bg-white/10 flex items-center justify-center">
                        <Clock className="size-5" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Tempo de Resposta</p>
                        <p className="text-sm font-bold">Até 48 horas úteis</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="size-10 rounded-xl bg-white/10 flex items-center justify-center">
                        <MapPin className="size-5" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Localização</p>
                        <p className="text-sm font-bold">Brasil (Operação 100% Remota)</p>
                      </div>
                    </div>
                  </div>

                  <button className="w-full py-4 bg-blue-600 rounded-2xl font-black text-sm hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2 group">
                    <MessageCircle className="size-4 group-hover:rotate-12 transition-transform" />
                    Enviar Mensagem
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
